import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './ReadingDetailsForm.css';

const BIBLE_TRANSLATIONS = [
  { value: 'ESV', label: 'ESV (English Standard Version)' },
  { value: 'NIV', label: 'NIV (New International Version)' },
  { value: 'NASB', label: 'NASB (New American Standard)' },
  { value: 'KJV', label: 'KJV (King James Version)' },
  { value: 'NKJV', label: 'NKJV (New King James)' },
  { value: 'NLT', label: 'NLT (New Living Translation)' },
  { value: 'CSB', label: 'CSB (Christian Standard Bible)' },
];

const READING_METHODS = [
  { value: 'paper', label: 'Paper Bible' },
  { value: 'digital', label: 'Digital/App' },
  { value: 'audio', label: 'Audio Bible' },
  { value: 'mixed', label: 'Mixed methods' },
];

const COMPREHENSION_LEVELS = [
  { value: 'poor', label: 'Poor - Had trouble understanding' },
  { value: 'fair', label: 'Fair - Understood some parts' },
  { value: 'good', label: 'Good - Understood most of it' },
  { value: 'excellent', label: 'Excellent - Clear understanding' },
];

const EMOTIONAL_IMPACTS = [
  { value: 'none', label: 'None' },
  { value: 'mild', label: 'Mild - Slight emotional response' },
  { value: 'moderate', label: 'Moderate - Notable emotional impact' },
  { value: 'strong', label: 'Strong - Deep emotional impact' },
];

const TIME_OF_DAY_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
];

const DISTRACTION_LEVELS = [
  { value: 'none', label: 'None - Fully focused' },
  { value: 'minimal', label: 'Minimal - Slight distractions' },
  { value: 'moderate', label: 'Moderate - Some distractions' },
  { value: 'high', label: 'High - Many distractions' },
];

const STUDY_AIDS_OPTIONS = [
  'Commentary',
  'Concordance',
  'Cross-references',
  'Study Bible notes',
  'Bible dictionary',
  'Historical context',
  'Original language study',
  'Devotional material',
  'Other'
];

const ReadingDetailsForm = ({ 
  formData, 
  onChange, 
  showDetails = false, 
  onToggleDetails,
  compact = false 
}) => {
  const { preferences } = useAuth();
  const [customStudyAid, setCustomStudyAid] = useState('');

  // Apply user defaults when form data is empty
  useEffect(() => {
    if (preferences && Object.keys(formData).length === 0) {
      const defaults = {
        translation: preferences.bibleTranslation || 'ESV',
        readingMethod: preferences.defaultReadingMethod || null,
        audioSpeed: preferences.defaultAudioSpeed || null,
        location: preferences.defaultLocation || '',
        timeOfDay: preferences.defaultTimeOfDay || null,
        studyAids: preferences.defaultStudyAids || [],
        readingDuration: null,
        comprehensionLevel: null,
        emotionalImpact: null,
        distractions: null,
        prayerTime: null,
        journaling: null
      };
      onChange(defaults);
    }
  }, [preferences, formData, onChange]);

  const handleFieldChange = (field, value) => {
    // Convert empty strings to null for API compatibility
    const processedValue = value === '' ? null : value;
    onChange({
      ...formData,
      [field]: processedValue
    });
  };

  const handleStudyAidToggle = (aid) => {
    const currentAids = formData.studyAids || [];
    const newAids = currentAids.includes(aid)
      ? currentAids.filter(a => a !== aid)
      : [...currentAids, aid];
    
    handleFieldChange('studyAids', newAids);
  };

  const handleAddCustomStudyAid = () => {
    if (customStudyAid.trim()) {
      const currentAids = formData.studyAids || [];
      handleFieldChange('studyAids', [...currentAids, customStudyAid.trim()]);
      setCustomStudyAid('');
    }
  };

  const handleRemoveStudyAid = (aidToRemove) => {
    const currentAids = formData.studyAids || [];
    handleFieldChange('studyAids', currentAids.filter(aid => aid !== aidToRemove));
  };

  if (!showDetails) {
    return (
      <div className="reading-details-toggle">
        <button 
          type="button" 
          className="toggle-details-btn"
          onClick={onToggleDetails}
        >
          Add reading details
        </button>
      </div>
    );
  }

  return (
    <div className={`reading-details-form ${compact ? 'compact' : ''}`}>
      <div className="details-header">
        <h3>Additional details</h3>
        <button 
          type="button" 
          className="toggle-details-btn"
          onClick={onToggleDetails}
        >
          ✕ Hide
        </button>
      </div>

      <div className="details-grid">
        {/* Translation */}
        <div className="form-group">
          <label htmlFor="translation">Bible translation</label>
          <select
            id="translation"
            value={formData.translation || ''}
            onChange={(e) => handleFieldChange('translation', e.target.value)}
          >
            <option value="">Select translation...</option>
            {BIBLE_TRANSLATIONS.map(translation => (
              <option key={translation.value} value={translation.value}>
                {translation.label}
              </option>
            ))}
          </select>
        </div>

        {/* Reading Method */}
        <div className="form-group">
          <label htmlFor="readingMethod">Reading method</label>
          <select
            id="readingMethod"
            value={formData.readingMethod || ''}
            onChange={(e) => handleFieldChange('readingMethod', e.target.value)}
          >
            <option value="">Select method...</option>
            {READING_METHODS.map(method => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>

        {/* Audio Speed (conditional) */}
        {formData.readingMethod === 'audio' && (
          <div className="form-group">
            <label htmlFor="audioSpeed">Audio speed</label>
            <select
              id="audioSpeed"
              value={formData.audioSpeed || ''}
              onChange={(e) => handleFieldChange('audioSpeed', e.target.value)}
            >
              <option value="">Select speed...</option>
              <option value="0.5">0.5x (Slow)</option>
              <option value="0.75">0.75x</option>
              <option value="1.0">1.0x (Normal)</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2.0">2.0x (Fast)</option>
              <option value="3.0">3.0x (Very fast)</option>
            </select>
          </div>
        )}

        {/* Reading Duration */}
        <div className="form-group">
          <label htmlFor="readingDuration">Reading duration (minutes)</label>
          <input
            type="number"
            id="readingDuration"
            min="1"
            max="1440"
            value={formData.readingDuration || ''}
            onChange={(e) => handleFieldChange('readingDuration', e.target.value)}
            placeholder="e.g., 15"
          />
        </div>

        {/* Location */}
        <div className="form-group">
          <label htmlFor="location">Location</label>
          <input
            type="text"
            id="location"
            maxLength="100"
            value={formData.location || ''}
            onChange={(e) => handleFieldChange('location', e.target.value)}
            placeholder="e.g., Home, Church, Work"
          />
        </div>

        {/* Time of Day */}
        <div className="form-group">
          <label htmlFor="timeOfDay">Time of day</label>
          <select
            id="timeOfDay"
            value={formData.timeOfDay || ''}
            onChange={(e) => handleFieldChange('timeOfDay', e.target.value)}
          >
            <option value="">Select time...</option>
            {TIME_OF_DAY_OPTIONS.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
        </div>

        {/* Comprehension Level */}
        <div className="form-group">
          <label htmlFor="comprehensionLevel">Comprehension level</label>
          <select
            id="comprehensionLevel"
            value={formData.comprehensionLevel || ''}
            onChange={(e) => handleFieldChange('comprehensionLevel', e.target.value)}
          >
            <option value="">Select level...</option>
            {COMPREHENSION_LEVELS.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Emotional Impact */}
        <div className="form-group">
          <label htmlFor="emotionalImpact">Emotional impact</label>
          <select
            id="emotionalImpact"
            value={formData.emotionalImpact || ''}
            onChange={(e) => handleFieldChange('emotionalImpact', e.target.value)}
          >
            <option value="">Select impact...</option>
            {EMOTIONAL_IMPACTS.map(impact => (
              <option key={impact.value} value={impact.value}>
                {impact.label}
              </option>
            ))}
          </select>
        </div>

        {/* Distractions */}
        <div className="form-group">
          <label htmlFor="distractions">Distraction level</label>
          <select
            id="distractions"
            value={formData.distractions || ''}
            onChange={(e) => handleFieldChange('distractions', e.target.value)}
          >
            <option value="">Select level...</option>
            {DISTRACTION_LEVELS.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prayer Time */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.prayerTime || false}
              onChange={(e) => handleFieldChange('prayerTime', e.target.checked)}
            />
            <span>Included prayer time</span>
          </label>
        </div>

        {/* Journaling */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.journaling || false}
              onChange={(e) => handleFieldChange('journaling', e.target.checked)}
            />
            <span>Did journaling</span>
          </label>
        </div>
      </div>

      {/* Study Aids */}
      <div className="form-group study-aids-section">
        <label>Study aids used</label>
        <div className="study-aids-grid">
          {STUDY_AIDS_OPTIONS.map(aid => (
            <label key={aid} className="checkbox-label">
              <input
                type="checkbox"
                checked={(formData.studyAids || []).includes(aid)}
                onChange={() => handleStudyAidToggle(aid)}
              />
              <span>{aid}</span>
            </label>
          ))}
        </div>
        
        {/* Custom Study Aid */}
        <div className="custom-study-aid">
          <input
            type="text"
            value={customStudyAid}
            onChange={(e) => setCustomStudyAid(e.target.value)}
            placeholder="Add custom study aid..."
            maxLength="50"
            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomStudyAid()}
          />
          <button 
            type="button" 
            onClick={handleAddCustomStudyAid}
            disabled={!customStudyAid.trim()}
          >
            Add
          </button>
        </div>

        {/* Selected Study Aids */}
        {(formData.studyAids || []).length > 0 && (
          <div className="selected-study-aids">
            <label>Selected:</label>
            <div className="study-aids-tags">
              {formData.studyAids.map(aid => (
                <span key={aid} className="study-aid-tag">
                  {aid}
                  <button
                    type="button"
                    onClick={() => handleRemoveStudyAid(aid)}
                    className="remove-aid-btn"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingDetailsForm;
